import { useState } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  Calendar,
  Linkedin,
  Building2,
  Clock,
  FileText,
  MessageSquare,
  User,
  Tags,
  StickyNote,
  Send,
  Video,
  AlertTriangle,
} from 'lucide-react';
import { fetchCandidate, fetchCandidateTags, fetchCandidateFiles, fetchCandidateApplications } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { TagSelector } from '@/components/tags';
import { FileUploader, FileList } from '@/components/files';
import { NotesSection } from '@/components/notes';
import { AssignToJobDialog } from '@/components/candidates';
import { EmailComposer } from '@/components/email/EmailComposer';
import { EmailHistoryList } from '@/components/email/EmailHistoryList';
import { InterviewList } from '@/components/interviews/InterviewList';
import { InterviewScheduler } from '@/components/interviews/InterviewScheduler';
import { STAGE_LABELS, PipelineStage } from '@/types';
import { getInitials, formatStatusLabel } from '@/lib/utils';

function getStatusBadgeVariant(status: string): 'default' | 'secondary' | 'success' | 'warning' | 'destructive' {
  switch (status) {
    case 'complete':
      return 'success';
    case 'in_progress':
      return 'default';
    case 'stalled':
      return 'warning';
    case 'new':
    default:
      return 'secondary';
  }
}

function formatDate(dateString: string | null): string {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function CandidateProfileSkeleton() {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Skeleton className="h-8 w-8 rounded" />
        <Skeleton className="h-6 w-32" />
      </div>
      <div className="flex items-start gap-6">
        <Skeleton className="h-20 w-20 rounded-full" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-5 w-64" />
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Skeleton className="h-64 lg:col-span-2" />
        <Skeleton className="h-64" />
      </div>
    </div>
  );
}

export function CandidateProfilePage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const attentionContext = (location.state as { attentionContext?: { applicationId: string; jobId: string; jobTitle: string; currentStage: PipelineStage; daysStuck: number } })?.attentionContext;
  const [activeTab, setActiveTab] = useState('profile');
  const [assignJobOpen, setAssignJobOpen] = useState(false);
  const [emailComposerOpen, setEmailComposerOpen] = useState(false);
  const [interviewSchedulerOpen, setInterviewSchedulerOpen] = useState(false);

  const {
    data: candidate,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['candidate', id],
    queryFn: () => fetchCandidate(id!),
    enabled: !!id,
  });

  const { data: candidateTags = [] } = useQuery({
    queryKey: ['candidateTags', id],
    queryFn: () => fetchCandidateTags(id!),
    enabled: !!id,
  });

  const { data: candidateFiles = [], isLoading: isLoadingFiles } = useQuery({
    queryKey: ['candidateFiles', id],
    queryFn: () => fetchCandidateFiles(id!),
    enabled: !!id,
  });

  const { data: applicationsData } = useQuery({
    queryKey: ['candidateApplications', id],
    queryFn: () => fetchCandidateApplications(id!),
    enabled: !!id,
  });

  if (isLoading) {
    return <CandidateProfileSkeleton />;
  }

  if (error || !candidate) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-text-primary">Candidate Not Found</h2>
          <p className="text-text-tertiary mt-2">
            The candidate you are looking for does not exist or has been removed.
          </p>
          <Button asChild className="mt-4">
            <Link to="/candidates">Back to Candidates</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <div>
        <Link
          to="/candidates"
          className="inline-flex items-center gap-2 text-sm text-text-tertiary hover:text-text-primary"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Candidates
        </Link>
      </div>

      {/* Attention Banner - shown when navigating from Needs Attention */}
      {attentionContext && (
        <div className="flex items-center gap-3 p-4 rounded-lg border border-amber-500/30 bg-amber-500/10">
          <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0" />
          <div className="flex-1 text-sm">
            <span className="font-medium text-amber-500">Needs Attention:</span>{' '}
            <span className="text-text-secondary">
              Stuck at <span className="font-medium text-text-primary">{STAGE_LABELS[attentionContext.currentStage] || attentionContext.currentStage}</span> for{' '}
              <span className="font-medium text-text-primary">{attentionContext.daysStuck} days</span> on{' '}
              <Link to={`/jobs/${attentionContext.jobId}/pipeline`} className="font-medium text-emerald-500 hover:underline">
                {attentionContext.jobTitle}
              </Link>
            </span>
          </div>
        </div>
      )}

      {/* Profile Header */}
      <div className="bg-bg-tertiary rounded-xl border border-white/10 shadow-raised surface-sheen p-6">
        <div className="flex flex-col sm:flex-row items-start gap-6">
          <Avatar className="h-20 w-20">
            <AvatarFallback className="text-2xl">
              {getInitials(candidate.firstName, candidate.lastName)}
            </AvatarFallback>
          </Avatar>

          <div className="flex-1">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <h1 className="text-2xl font-bold text-text-primary">
                {candidate.firstName} {candidate.lastName}
              </h1>
              <Badge variant={getStatusBadgeVariant(candidate.onboardingStatus)}>
                {formatStatusLabel(candidate.onboardingStatus)}
              </Badge>
            </div>

            {candidate.currentJobTitle && (
              <p className="text-lg text-text-secondary mt-1">
                {candidate.currentJobTitle}
                {candidate.currentEmployer && ` at ${candidate.currentEmployer}`}
              </p>
            )}

            <div className="flex flex-wrap gap-4 mt-4 text-sm text-text-tertiary">
              {candidate.currentLocation && (
                <div className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  {candidate.currentLocation}
                </div>
              )}
              {candidate.email && (
                <a
                  href={`mailto:${candidate.email}`}
                  className="flex items-center gap-1 hover:text-emerald-500"
                >
                  <Mail className="h-4 w-4" />
                  {candidate.email}
                </a>
              )}
              <a
                href={`tel:${candidate.whatsappPhone}`}
                className="flex items-center gap-1 hover:text-emerald-500"
              >
                <Phone className="h-4 w-4" />
                {candidate.whatsappPhone}
              </a>
              {candidate.linkedinUrl && (
                <a
                  href={candidate.linkedinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 hover:text-emerald-500"
                >
                  <Linkedin className="h-4 w-4" />
                  LinkedIn
                </a>
              )}
            </div>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" asChild>
              <Link to={`/candidates/${id}/edit`}>Edit Profile</Link>
            </Button>
            <Button onClick={() => {
              setActiveTab('email');
              setEmailComposerOpen(true);
            }}>
              <Send className="h-4 w-4 mr-2" />
              Send Email
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="profile" className="gap-2">
            <User className="h-4 w-4" />
            Profile
          </TabsTrigger>
          <TabsTrigger value="documents" className="gap-2">
            <FileText className="h-4 w-4" />
            Documents
          </TabsTrigger>
          <TabsTrigger value="jobs" className="gap-2">
            <Briefcase className="h-4 w-4" />
            Jobs
          </TabsTrigger>
          <TabsTrigger value="notes" className="gap-2">
            <StickyNote className="h-4 w-4" />
            Notes
          </TabsTrigger>
          <TabsTrigger value="email" className="gap-2">
            <Send className="h-4 w-4" />
            Email
          </TabsTrigger>
          <TabsTrigger value="interviews" className="gap-2">
            <Video className="h-4 w-4" />
            Interviews
          </TabsTrigger>
          <TabsTrigger value="conversations" className="gap-2">
            <MessageSquare className="h-4 w-4" />
            Conversations
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Info */}
            <div className="lg:col-span-2 space-y-6">
              {/* Skills */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Skills</CardTitle>
                </CardHeader>
                <CardContent>
                  {candidate.skills && candidate.skills.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {candidate.skills.map((skill) => (
                        <Badge key={skill} variant="secondary">
                          {skill}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-text-tertiary">No skills listed</p>
                  )}
                </CardContent>
              </Card>

              {/* Experience */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Experience</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <Briefcase className="h-5 w-5 text-text-tertiary mt-0.5" />
                      <div>
                        <p className="font-medium">Current Position</p>
                        <p className="text-text-secondary">
                          {candidate.currentJobTitle || 'Not specified'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Building2 className="h-5 w-5 text-text-tertiary mt-0.5" />
                      <div>
                        <p className="font-medium">Current Employer</p>
                        <p className="text-text-secondary">
                          {candidate.currentEmployer || 'Not specified'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Clock className="h-5 w-5 text-text-tertiary mt-0.5" />
                      <div>
                        <p className="font-medium">Years of Experience</p>
                        <p className="text-text-secondary">
                          {candidate.yearsExperience !== null
                            ? `${candidate.yearsExperience} years`
                            : 'Not specified'}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Preferences */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Job Preferences</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <p className="font-medium text-sm text-text-tertiary">Preferred Job Types</p>
                      {candidate.preferredJobTypes && candidate.preferredJobTypes.length > 0 ? (
                        <div className="flex flex-wrap gap-2 mt-1">
                          {candidate.preferredJobTypes.map((type) => (
                            <Badge key={type} variant="outline">
                              {type}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <p className="text-text-secondary mt-1">Not specified</p>
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-sm text-text-tertiary">Expected Salary Range</p>
                      <p className="text-text-primary mt-1">
                        {candidate.expectedSalaryRange || 'Not specified'}
                      </p>
                    </div>
                    <div>
                      <p className="font-medium text-sm text-text-tertiary">Availability</p>
                      <p className="text-text-primary mt-1">
                        {candidate.availabilityDate
                          ? formatDate(candidate.availabilityDate)
                          : 'Not specified'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Tags */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Tags className="h-5 w-5" />
                    Tags
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <TagSelector candidateId={id!} assignedTags={candidateTags} />
                </CardContent>
              </Card>

              {/* Personal Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Personal Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <dl className="space-y-4">
                    <div>
                      <dt className="text-sm font-medium text-text-tertiary">Nationality</dt>
                      <dd className="text-text-primary mt-1">
                        {candidate.nationality || 'Not specified'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-text-tertiary">Secondary Phone</dt>
                      <dd className="text-text-primary mt-1">
                        {candidate.phoneSecondary || 'Not provided'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-text-tertiary">Source</dt>
                      <dd className="text-text-primary mt-1">{candidate.source}</dd>
                    </div>
                  </dl>
                </CardContent>
              </Card>

              {/* Timeline */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Timeline</CardTitle>
                </CardHeader>
                <CardContent>
                  <dl className="space-y-4">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-text-tertiary" />
                      <div>
                        <dt className="text-sm font-medium text-text-tertiary">Created</dt>
                        <dd className="text-text-primary">{formatDate(candidate.createdAt)}</dd>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-text-tertiary" />
                      <div>
                        <dt className="text-sm font-medium text-text-tertiary">Last Updated</dt>
                        <dd className="text-text-primary">{formatDate(candidate.updatedAt)}</dd>
                      </div>
                    </div>
                  </dl>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents">
          <div className="space-y-6">
            {/* File Uploader */}
            <Card>
              <CardHeader>
                <CardTitle>Upload Documents</CardTitle>
              </CardHeader>
              <CardContent>
                <FileUploader candidateId={id!} />
              </CardContent>
            </Card>

            {/* File List */}
            <Card>
              <CardHeader>
                <CardTitle>Uploaded Documents</CardTitle>
              </CardHeader>
              <CardContent>
                <FileList
                  files={candidateFiles}
                  candidateId={id!}
                  isLoading={isLoadingFiles}
                />
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Notes Tab */}
        <TabsContent value="notes">
          <Card>
            <CardHeader>
              <CardTitle>Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <NotesSection entityType="candidate" entityId={id!} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Jobs Tab */}
        <TabsContent value="jobs">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Job Applications</CardTitle>
                <Button size="sm" onClick={() => setAssignJobOpen(true)}>
                  <Briefcase className="h-4 w-4 mr-2" />
                  Add to Job
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {applicationsData && applicationsData.data.length > 0 ? (
                <div className="space-y-3">
                  {applicationsData.data.map((application) => (
                    <Link
                      key={application.id}
                      to={`/jobs/${application.jobId}`}
                      className="flex items-center justify-between p-4 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Briefcase className="h-5 w-5 text-text-tertiary" />
                        <div>
                          <div className="font-medium text-text-primary">
                            {application.job?.title || 'Unknown Job'}
                          </div>
                          {application.job?.company && (
                            <div className="text-sm text-text-tertiary">
                              {application.job.company.name}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-text-tertiary">
                          {formatDate(application.createdAt)}
                        </span>
                        <Badge variant="secondary">
                          {STAGE_LABELS[application.currentStage] || application.currentStage}
                        </Badge>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-48 text-text-tertiary">
                  <Briefcase className="h-12 w-12 mb-2" />
                  <p>No job applications yet</p>
                  <Button variant="outline" className="mt-4" onClick={() => setAssignJobOpen(true)}>
                    Add to Job
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
          <AssignToJobDialog
            candidateId={id!}
            open={assignJobOpen}
            onOpenChange={setAssignJobOpen}
          />
        </TabsContent>

        {/* Email Tab */}
        <TabsContent value="email">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Email</CardTitle>
                  <Button size="sm" onClick={() => setEmailComposerOpen(true)}>
                    <Send className="h-4 w-4 mr-2" />
                    Compose
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <EmailHistoryList candidateId={id!} />
              </CardContent>
            </Card>
            <EmailComposer
              open={emailComposerOpen}
              onOpenChange={setEmailComposerOpen}
              candidateId={id!}
              candidateEmail={candidate.email ?? undefined}
            />
          </div>
        </TabsContent>

        {/* Interviews Tab */}
        <TabsContent value="interviews">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Interviews</CardTitle>
                  <Button size="sm" onClick={() => setInterviewSchedulerOpen(true)}>
                    <Video className="h-4 w-4 mr-2" />
                    Schedule
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <InterviewList candidateId={id!} />
              </CardContent>
            </Card>
            <InterviewScheduler
              open={interviewSchedulerOpen}
              onOpenChange={setInterviewSchedulerOpen}
              onSuccess={() => setInterviewSchedulerOpen(false)}
              candidateId={id!}
            />
          </div>
        </TabsContent>

        {/* Conversations Tab */}
        <TabsContent value="conversations">
          <Card>
            <CardHeader>
              <CardTitle>Conversation History</CardTitle>
            </CardHeader>
            <CardContent>
              {(candidate as unknown as Record<string, unknown>).conversations &&
               Array.isArray((candidate as unknown as Record<string, unknown>).conversations) &&
               ((candidate as unknown as Record<string, unknown>).conversations as Array<{
                 id: string;
                 status: string;
                 lastMessageAt: string;
                 createdAt: string;
                 messages: Array<{ role: string; content: string; timestamp: string }>;
               }>).length > 0 ? (
                <div className="space-y-4">
                  {((candidate as unknown as Record<string, unknown>).conversations as Array<{
                    id: string;
                    status: string;
                    lastMessageAt: string;
                    createdAt: string;
                    messages: Array<{ role: string; content: string; timestamp: string }>;
                  }>).map((conversation) => (
                    <div
                      key={conversation.id}
                      className="rounded-lg border border-white/10 bg-white/5 p-4 space-y-3"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <MessageSquare className="h-4 w-4 text-text-tertiary" />
                          <span className="text-sm font-medium text-text-primary">
                            Conversation
                          </span>
                          <Badge variant="secondary">{conversation.status}</Badge>
                        </div>
                        <span className="text-xs text-text-tertiary">
                          {formatDate(conversation.lastMessageAt || conversation.createdAt)}
                        </span>
                      </div>
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {conversation.messages.map((msg, idx) => (
                          <div
                            key={idx}
                            className={`text-sm p-2 rounded ${
                              msg.role === 'assistant'
                                ? 'bg-emerald-500/10 text-text-secondary'
                                : 'bg-white/5 text-text-primary'
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs font-medium text-text-tertiary">
                                {msg.role === 'assistant' ? 'Bot' : 'Candidate'}
                              </span>
                              {msg.timestamp && (
                                <span className="text-xs text-text-tertiary">
                                  {new Date(msg.timestamp).toLocaleString()}
                                </span>
                              )}
                            </div>
                            <p className="whitespace-pre-wrap">{msg.content}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-48 text-text-tertiary">
                  <MessageSquare className="h-12 w-12 mb-2" />
                  <p>No conversations recorded</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
